import { BackendClient, ClientType } from 'backend-client-web';
import { newUuid } from 'client-core';

import getHeyloginVersion from './getHeyloginVersion';

const CLIENT_TYPE = ClientType.WEB;

let metaUrlsCache:
  | {
      backendServer: string | undefined;
      auditlogServer: string | undefined;
      centrifugeServer: string | undefined;
      shareSecretServer: string | undefined;
      publicDataServer: string | undefined;
      sharelinkServer: string | undefined;
    }
  | undefined;

function getMetaUrls() {
  if (!metaUrlsCache) {
    metaUrlsCache = {
      // these meta elements may be injected into the build html as part of the postprocessing step in CI
      backendServer: document.querySelector<HTMLMetaElement>('meta[name=backend-server]')?.content,
      auditlogServer: document.querySelector<HTMLMetaElement>('meta[name=auditlog-server]')
        ?.content,
      centrifugeServer: document.querySelector<HTMLMetaElement>('meta[name=centrifuge-server]')
        ?.content,
      shareSecretServer: document.querySelector<HTMLMetaElement>('meta[name=share-secret-server]')
        ?.content,
      publicDataServer: document.querySelector<HTMLMetaElement>('meta[name=public-data-server]')
        ?.content,
      sharelinkServer: document.querySelector<HTMLMetaElement>('meta[name=sharelink-server]')
        ?.content,
    };
  }
  return metaUrlsCache;
}

function getBackendUrl() {
  if (import.meta.env.VITE_BACKEND_SERVER) {
    return import.meta.env.VITE_BACKEND_SERVER;
  }
  const metaUrls = getMetaUrls();
  if (metaUrls.backendServer) {
    return metaUrls.backendServer;
  }
  return `${getProtocol()}//${window.location.host}/api/v1`;
}

function getAuditlogUrl() {
  if (import.meta.env.VITE_AUDITLOG_SERVER) {
    return import.meta.env.VITE_AUDITLOG_SERVER;
  }
  const metaUrls = getMetaUrls();
  if (metaUrls.auditlogServer) {
    return metaUrls.auditlogServer;
  }
  return `${getProtocol()}//log.${window.location.host}/api/v1`;
}

export function getCentrifugeUrl() {
  if (import.meta.env.VITE_CENTRIFUGE_SERVER) {
    return import.meta.env.VITE_CENTRIFUGE_SERVER;
  }
  const metaUrls = getMetaUrls();
  if (metaUrls.centrifugeServer) {
    return metaUrls.centrifugeServer;
  }
  return `${getProtocol()}//sync.${window.location.host}`;
}

export function getShareSecretUrlPath() {
  if (import.meta.env.VITE_SHARE_SECRET_SERVER) {
    return `${import.meta.env.VITE_SHARE_SECRET_SERVER}/secret`;
  }
  const metaUrls = getMetaUrls();
  if (metaUrls.shareSecretServer) {
    return `${metaUrls.shareSecretServer}/secret`;
  }
  return `${getProtocol()}//api.${window.location.host}/secret`;
}

export function getShareLinkUrl() {
  if (import.meta.env.VITE_SHARELINK_SERVER) {
    return import.meta.env.VITE_SHARELINK_SERVER;
  }
  const metaUrls = getMetaUrls();
  if (metaUrls.sharelinkServer) {
    return metaUrls.sharelinkServer;
  }
  return `${getProtocol()}//${window.location.host}/share`;
}

/**
 * Unlike the other addresses there is no host to derive this one from: the data sets are not
 * necessarily served by the deployment the app was loaded from, and guessing would mean an
 * on-premise installation could end up querying heylogin's own data host.
 */
export function getPublicDataUrl() {
  if (import.meta.env.VITE_PUBLIC_DATA_SERVER) {
    return import.meta.env.VITE_PUBLIC_DATA_SERVER;
  }
  const metaUrls = getMetaUrls();
  if (metaUrls.publicDataServer) {
    return metaUrls.publicDataServer;
  }
  throw new Error('No public data server configured!');
}

// derived urls take the protocol the webapp was loaded over, to prevent mixed content problems
function getProtocol() {
  return window.location.protocol;
}

export default function getBackendClient(clientType: ClientType = CLIENT_TYPE) {
  return new BackendClient({
    backendBaseUri: getBackendUrl(),
    auditlogBaseUri: getAuditlogUrl(),
    clientId: newUuid(),
    clientType,
    clientVersion: getHeyloginVersion(),
    syncSuperUsers: true,
  });
}
